import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Play, Pause, SkipForward, Mic, LogIn, ChevronRight, Search, User, Share2, Volume2, Filter, Clock, Users, Shuffle, Repeat } from 'lucide-react';
import { audioApi, comboApi, followApi, getAudioFileUrl } from '@/lib/api';
import { AUDIO_COLORS, AUDIO_SHAPES, LANGUAGE_FLAGS } from '@/lib/constants';
import { useAuth } from '@/contexts/AuthContext';
import { shareAudio } from '@/lib/share';
import { addToHistorial } from './HistorialPage';
import { AudioVisualizer } from '@/components/audio/AudioVisualizer';
import { AudioVisualBadge } from '@/components/audio/AudioVisuals';

// Combos predefinidos - 6 PRINCIPALES (actualizados)
const STANDARD_COMBOS = [
  { id: 'morning', name: 'Buenos días', emoji: '☀️', color: '#F59E0B', desc: 'Clima, tráfico y motivación' },
  { id: 'fitness', name: 'Deporte y Salud', emoji: '💪', color: '#10B981', desc: 'Ejercicio y nutrición' },
  { id: 'humor', name: 'Familia y Amigos', emoji: '👨‍👩‍👧', color: '#3B82F6', desc: 'Mensajes y planes' },
  { id: 'couple', name: 'Pareja y Amor', emoji: '❤️', color: '#EF4444', desc: 'Citas y romanticismo' },
  { id: 'money', name: 'Trabajo y Estudio', emoji: '💼', color: '#8B5CF6', desc: 'Productividad' },
  { id: 'night', name: 'Estacional', emoji: '🎉', color: '#EAB308', desc: 'Eventos y fiestas' },
];

const HomePage = () => {
  const { user, login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [audios, setAudios] = useState([]);
  const [followingAudios, setFollowingAudios] = useState([]);
  const [filteredAudios, setFilteredAudios] = useState([]);
  const [combos, setCombos] = useState([]);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedShape, setSelectedShape] = useState(null);
  const [showGlossary, setShowGlossary] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [feedTab, setFeedTab] = useState('todos'); // 'todos' | 'siguiendo'
  const [searchQuery, setSearchQuery] = useState('');
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [openFilterGroup, setOpenFilterGroup] = useState(null); // 'tematica' | 'tipo' | 'idioma' | null
  const [feedSort, setFeedSort] = useState('recent'); // 'recent' | 'popular'
  const [topUsers, setTopUsers] = useState([]);
  const audioRef = useRef(null);
  const nextAudioRef = useRef(null); // Pre-carga del siguiente audio

  const toggleCategory = (key) => {
    setSelectedCategories(prev =>
      prev.includes(key) ? prev.filter(c => c !== key) : [...prev, key]
    );
  };

  const clearFilters = () => {
    setSelectedCategories([]);
    setSelectedLanguage(null);
    setSelectedShape(null);
  };

  const hasActiveFilters = selectedCategories.length > 0 || !!selectedLanguage || !!selectedShape;

  useEffect(() => {
    loadData();
  }, []);

  // Filtrar audios cuando cambia categoría, idioma o tab
  useEffect(() => {
    const baseAudios = feedTab === 'siguiendo' ? followingAudios : audios;
    let result = [...baseAudios];
    if (selectedCategories.length > 0) {
      result = result.filter(a => selectedCategories.includes(a.color));
    }
    if (selectedShape) {
      result = result.filter(a => a.shape === selectedShape);
    }
    if (selectedLanguage) {
      result = result.filter(a => a.language === selectedLanguage);
    }
    if (feedSort === 'popular') {
      result.sort((a, b) =>
        ((b.plays_count || 0) * 2 + (b.useful_count || 0) * 3 + (b.saves_count || 0)) -
        ((a.plays_count || 0) * 2 + (a.useful_count || 0) * 3 + (a.saves_count || 0))
      );
    } else {
      result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
    setFilteredAudios(result);
  }, [audios, followingAudios, selectedCategories, selectedShape, selectedLanguage, feedTab, feedSort]);

  const loadData = async () => {
    try {
      const [audioData, comboData] = await Promise.all([
        audioApi.getFeed({ limit: 100 }), // Cargar más para reproducción infinita
        comboApi.getFeed(0, 10)
      ]);
      
      const shuffled = [...audioData].sort(() => Math.random() - 0.5);
      setAudios(shuffled);
      setFilteredAudios(shuffled);
      setCombos(comboData);

      // Cargar ranking de creadores
      try {
        const topData = await followApi.getTopUsers(10);
        setTopUsers(topData);
      } catch (e) {
        console.log('No top users');
      }
      
      // Cargar feed de siguiendo si está autenticado
      if (isAuthenticated) {
        try {
          const followingData = await followApi.getFeed(100);
          setFollowingAudios(followingData);
        } catch (e) {
          console.log('No following feed');
        }
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
    }
  };

  const playAudio = (audio, idx) => {
    // Si es el mismo audio que está sonando, toggle play/pause
    if (currentAudio?.audio_id === audio.audio_id) {
      setIsPlaying(!isPlaying);
      return;
    }
    
    // Nuevo audio
    setCurrentAudio(audio);
    setCurrentIndex(idx);
    setIsPlaying(true);
    setProgress(0);
    
    // Guardar en historial
    addToHistorial(audio);
    
    // Pre-cargar el siguiente audio
    preloadNextAudio(idx);
  };

  // Empezar reproducción infinita directamente (manos libres)
  const startListening = () => {
    if (filteredAudios.length === 0) return;
    const idx = Math.floor(Math.random() * filteredAudios.length);
    playAudio(filteredAudios[idx], idx);
  };

  // Pre-carga del siguiente audio para transición sin cortes
  const preloadNextAudio = useCallback((currentIdx) => {
    const nextIdx = (currentIdx + 1) % filteredAudios.length;
    const nextAudio = filteredAudios[nextIdx];
    if (nextAudio && nextAudioRef.current) {
      nextAudioRef.current.src = getAudioFileUrl(nextAudio.audio_url);
      nextAudioRef.current.load();
    }
  }, [filteredAudios]);

  // Modo de reproducción: en orden por defecto; aleatorio (shuffle) opcional
  const [shuffleMode, setShuffleMode] = useState(false);

  // Reproducción infinita - siguiente audio automático
  const playNext = useCallback(() => {
    if (filteredAudios.length === 0) return;
    let nextIndex;
    if (!shuffleMode || filteredAudios.length === 1) {
      // En orden: siguiente de la lista (bucle)
      nextIndex = (currentIndex + 1) % filteredAudios.length;
    } else {
      // Aleatorio: evita repetir el actual
      do {
        nextIndex = Math.floor(Math.random() * filteredAudios.length);
      } while (nextIndex === currentIndex);
    }
    setCurrentIndex(nextIndex);
    setCurrentAudio(filteredAudios[nextIndex]);
    setIsPlaying(true);
    setProgress(0);
    preloadNextAudio(nextIndex);
  }, [currentIndex, filteredAudios, preloadNextAudio, shuffleMode]);

  useEffect(() => {
    if (audioRef.current && currentAudio) {
      audioRef.current.src = getAudioFileUrl(currentAudio.audio_url);
      if (isPlaying) {
        audioRef.current.play().catch(console.error);
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying, currentAudio]);

  // Actualizar progreso
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const current = audioRef.current.currentTime;
      const total = audioRef.current.duration || 1;
      setProgress((current / total) * 100);
      setDuration(total);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    const filtered = filteredAudios.filter(a => 
      a.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      AUDIO_COLORS[a.color]?.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    if (filtered.length > 0) {
      playAudio(filtered[0], 0);
    }
  };

  // Obtener emojis adicionales como etiquetas
  const getEmojiTags = (emojiStr) => {
    if (!emojiStr || emojiStr === '💬') return [];
    const emojis = [...emojiStr];
    return emojis.slice(0, 4);
  };

  // Formatear tiempo mm:ss
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 pb-36">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/5 border-b border-white/10">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <span className="text-xl">🎙️</span>
            </div>
            <span className="text-xl font-bold text-white">AudioToo</span>
          </div>
          
          <div className="flex items-center gap-1">
            {isAuthenticated ? (
              <>
                <Link to="/historial" className="text-white/70 p-2 hover:text-white" title="Historial">
                  <Clock size={22} />
                </Link>
                <Link to="/perfil" className="text-white/70 p-2 hover:text-white">
                  <User size={22} />
                </Link>
                <Link 
                  to="/grabar"
                  className="bg-gradient-to-r from-red-500 to-pink-500 text-white p-2.5 rounded-full shadow-lg"
                >
                  <Mic size={20} />
                </Link>
              </>
            ) : (
              <button 
                onClick={login}
                className="bg-white/10 backdrop-blur text-white px-4 py-2 rounded-full text-sm flex items-center gap-2 border border-white/20"
              >
                <LogIn size={16} />
                Entrar
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-6" style={{ paddingTop: currentAudio ? '7rem' : undefined }}>
        {/* Barra de búsqueda + Filtros */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-3 border border-white/20">
          <form onSubmit={(e) => { e.preventDefault(); handleSearch(); }} className="contents">
          <div className="flex items-center gap-2">
            <div className="flex-1 flex items-center gap-2 bg-white/10 rounded-xl px-3 py-2">
              <Search size={18} className="text-white/50" />
              <input
                type="search"
                enterKeyHint="search"
                placeholder="¿Qué quieres escuchar?"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 bg-transparent text-white placeholder-white/50 outline-none text-sm"
              />
            </div>
            <button 
              type="button"
              onClick={() => setShowFilters(!showFilters)} 
              className={`p-2 rounded-xl transition-colors ${showFilters ? 'bg-purple-500 text-white' : 'bg-white/10 text-white/70'}`}
            >
              <Filter size={18} />
            </button>
            <button type="submit" className="bg-purple-500 text-white p-2 rounded-xl">
              <Search size={18} />
            </button>
          </div>
          </form>

          {/* Filtros expandibles en acordeón */}
          {showFilters && (
            <div className="mt-3 pt-3 border-t border-white/10 space-y-1">
              {/* Temática (multi-selección: no se cierra al elegir chips) */}
              <div>
                <button
                  type="button"
                  onClick={() => setOpenFilterGroup(openFilterGroup === 'tematica' ? null : 'tematica')}
                  className="w-full flex items-center justify-between text-white/60 text-xs py-2 hover:text-white/80"
                >
                  <span>Filtrar por temática (varias){selectedCategories.length > 0 ? ` · ${selectedCategories.length}` : ''}</span>
                  <span>{openFilterGroup === 'tematica' ? '▾' : '▸'}</span>
                </button>
                {openFilterGroup === 'tematica' && (
                  <div className="flex flex-wrap gap-2 pb-2">
                    {Object.entries(AUDIO_COLORS).map(([key, color]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => toggleCategory(key)}
                        className={`px-2 py-1.5 rounded-full text-sm flex items-center gap-1 transition-all ${
                          selectedCategories.includes(key) ? 'bg-purple-500 text-white' : 'bg-white/10 text-white/70'
                        }`}
                      >
                        <span className="text-base">{color.emoji}</span>
                        {color.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Tipo (selección única: se repliega al elegir) */}
              <div>
                <button
                  type="button"
                  onClick={() => setOpenFilterGroup(openFilterGroup === 'tipo' ? null : 'tipo')}
                  className="w-full flex items-center justify-between text-white/60 text-xs py-2 hover:text-white/80"
                >
                  <span>Filtrar por tipo{selectedShape ? ' · 1' : ''}</span>
                  <span>{openFilterGroup === 'tipo' ? '▾' : '▸'}</span>
                </button>
                {openFilterGroup === 'tipo' && (
                  <div className="flex flex-wrap gap-2 pb-2">
                    <button
                      type="button"
                      onClick={() => { setSelectedShape(null); setOpenFilterGroup(null); }}
                      className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                        !selectedShape ? 'bg-purple-500 text-white' : 'bg-white/10 text-white/70'
                      }`}
                    >
                      Todos
                    </button>
                    {Object.entries(AUDIO_SHAPES).map(([key, shape]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => { setSelectedShape(key); setOpenFilterGroup(null); }}
                        className={`px-2 py-1.5 rounded-full text-sm flex items-center gap-1 transition-all ${
                          selectedShape === key ? 'bg-purple-500 text-white' : 'bg-white/10 text-white/70'
                        }`}
                      >
                        <span className="text-base">{shape.icon}</span>
                        {shape.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Idioma (selección única: se repliega al elegir) */}
              <div>
                <button
                  type="button"
                  onClick={() => setOpenFilterGroup(openFilterGroup === 'idioma' ? null : 'idioma')}
                  className="w-full flex items-center justify-between text-white/60 text-xs py-2 hover:text-white/80"
                >
                  <span>Filtrar por idioma{selectedLanguage ? ' · 1' : ''}</span>
                  <span>{openFilterGroup === 'idioma' ? '▾' : '▸'}</span>
                </button>
                {openFilterGroup === 'idioma' && (
                  <div className="flex flex-wrap gap-2 pb-2">
                    <button
                      type="button"
                      onClick={() => { setSelectedLanguage(null); setOpenFilterGroup(null); }}
                      className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                        !selectedLanguage ? 'bg-purple-500 text-white' : 'bg-white/10 text-white/70'
                      }`}
                    >
                      Todos
                    </button>
                    {Object.entries(LANGUAGE_FLAGS).map(([code, lang]) => (
                      <button
                        key={code}
                        type="button"
                        onClick={() => { setSelectedLanguage(code); setOpenFilterGroup(null); }}
                        className={`px-3 py-1.5 rounded-full text-sm flex items-center gap-1 transition-all ${
                          selectedLanguage === code ? 'bg-purple-500 text-white' : 'bg-white/10 text-white/70'
                        }`}
                      >
                        <span className="text-base">{lang.flag}</span>
                        {lang.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Botón Escuchar - manos libres */}
        <button
          onClick={startListening}
          disabled={filteredAudios.length === 0}
          className="w-full py-4 rounded-2xl bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold text-lg flex items-center justify-center gap-3 shadow-lg hover:scale-[1.01] transition-transform disabled:opacity-40"
          data-testid="listen-now-btn"
        >
          <Play size={24} />
          ▶ Escuchar ahora
        </button>

        {/* Combos COLORIDOS */}
        {!hasActiveFilters && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-white font-bold text-lg">🔥 Combos</h2>
            <Link to="/combos" className="text-purple-400 text-sm flex items-center gap-1">
              Ver todos <ChevronRight size={16} />
            </Link>
          </div>
          
          <div className="flex gap-2 overflow-x-auto pb-2">
            {STANDARD_COMBOS.map((combo) => (
              <Link key={combo.id} to={`/combo/combo_${combo.id}`} className="flex-shrink-0 w-28">
                <div 
                  className="rounded-2xl p-3 text-center hover:scale-105 transition-transform border border-white/10"
                  style={{ backgroundColor: combo.color }}
                >
                  <span className="text-3xl block mb-1 drop-shadow-lg">{combo.emoji}</span>
                  <p className="text-white text-xs font-bold truncate drop-shadow">{combo.name}</p>
                  <p className="text-white/70 text-[10px] truncate">{combo.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
        )}

        {/* Categorías/Temáticas */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-white font-bold text-lg">🎨 Temáticas</h2>
            <button
              onClick={() => setShowGlossary(!showGlossary)}
              className="text-white/60 text-xs flex items-center gap-1 bg-white/10 px-2 py-1 rounded-lg hover:bg-white/20"
            >
              ❓ ¿Qué significan?
            </button>
          </div>

          {/* Glosario de formas y colores (visible con y sin filtros activos) */}
          {showGlossary && (
            <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-3 mb-3 border border-white/20 text-xs">
              <p className="text-white font-bold mb-2">Formas (tipo de contenido):</p>
              <div className="grid grid-cols-2 gap-1 mb-3">
                {Object.entries(AUDIO_SHAPES).map(([key, shape]) => (
                  <div key={key} className="flex items-center gap-1.5 text-white/80">
                    <span className="text-base">{shape.icon}</span>
                    <span><strong>{shape.name}</strong> — {shape.description}</span>
                  </div>
                ))}
              </div>
              <p className="text-white font-bold mb-2">Colores (temáticas):</p>
              <div className="grid grid-cols-3 gap-1">
                {Object.entries(AUDIO_COLORS).map(([key, color]) => (
                  <div key={key} className="flex items-center gap-1.5 text-white/80">
                    <span className="text-base">{color.emoji}</span>
                    <span>{color.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {hasActiveFilters ? (
            <div className="flex flex-wrap gap-2">
              {selectedCategories.map(key => (
                <button
                  key={key}
                  onClick={() => toggleCategory(key)}
                  className="px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5 text-white"
                  style={{ backgroundColor: AUDIO_COLORS[key]?.hex }}
                >
                  <span>{AUDIO_COLORS[key]?.emoji}</span>
                  {AUDIO_COLORS[key]?.name}
                  <span className="text-white/70">✕</span>
                </button>
              ))}
              {selectedShape && (
                <button
                  onClick={() => setSelectedShape(null)}
                  className="px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5 bg-purple-500 text-white"
                >
                  <span>{AUDIO_SHAPES[selectedShape]?.icon}</span>
                  {AUDIO_SHAPES[selectedShape]?.name}
                  <span className="text-white/70">✕</span>
                </button>
              )}
              {selectedLanguage && (
                <button
                  onClick={() => setSelectedLanguage(null)}
                  className="px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5 bg-purple-500 text-white"
                >
                  {LANGUAGE_FLAGS[selectedLanguage]?.flag} {LANGUAGE_FLAGS[selectedLanguage]?.name}
                  <span className="text-white/70">✕</span>
                </button>
              )}
            </div>
          ) : (
            <>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {Object.entries(AUDIO_COLORS).map(([key, color]) => (
              <button
                key={key}
                onClick={() => toggleCategory(key)}
                className={`flex flex-col items-center p-1.5 rounded-xl transition-all flex-shrink-0 w-16 ${
                  selectedCategories.includes(key) ? 'ring-2 ring-white bg-white/20' : 'bg-white/5 hover:bg-white/10'
                }`}
              >
                <span 
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-lg"
                  style={{ backgroundColor: color.hex }}
                >
                  {color.emoji}
                </span>
                <span className="text-white/60 text-[9px] mt-1 truncate w-full text-center">{color.name}</span>
              </button>
            ))}
          </div>
            </>
          )}
        </section>

        {/* Top creadores */}
        {!hasActiveFilters && topUsers.length > 0 && (
          <section>
            <h2 className="text-white font-bold text-lg mb-3">🏆 Top creadores</h2>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {topUsers.map((u, i) => (
                <Link
                  key={u.user_id}
                  to={`/usuario/${u.user_id}`}
                  className="flex-shrink-0 w-28 bg-white/5 rounded-xl p-3 text-center hover:bg-white/10 border border-white/10"
                >
                  <span className={`text-sm font-bold ${i === 0 ? 'text-yellow-400' : i === 1 ? 'text-gray-300' : i === 2 ? 'text-amber-600' : 'text-white/40'}`}>
                    #{i + 1}
                  </span>
                  <div className="my-2 flex justify-center">
                    {u.user_picture ? (
                      <img src={u.user_picture} alt="" className="w-10 h-10 rounded-full" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold">
                        {u.username?.charAt(0)?.toUpperCase()}
                      </div>
                    )}
                  </div>
                  <p className="text-white text-xs font-medium truncate flex items-center justify-center gap-1">
                    {u.username}
                    {u.is_official && <span className="text-purple-400">✓</span>}
                  </p>
                  <p className="text-white/50 text-[10px]">{u.total_plays} reproducciones</p>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Feed de Audios - Lista con barra de progreso */}
        <section>
          {/* Tabs: Todos / Siguiendo */}
          {isAuthenticated && (
            <div className="flex gap-2 mb-3">
              <button
                onClick={() => setFeedTab('todos')}
                className={`flex-1 py-2 px-3 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  feedTab === 'todos' ? 'bg-purple-500 text-white' : 'bg-white/10 text-white/60'
                }`}
              >
                🌍 Todos
              </button>
              <button
                onClick={() => setFeedTab('siguiendo')}
                className={`flex-1 py-2 px-3 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  feedTab === 'siguiendo' ? 'bg-purple-500 text-white' : 'bg-white/10 text-white/60'
                }`}
              >
                <Users size={16} /> Siguiendo
              </button>
            </div>
          )}

          <div className="flex items-center justify-between mb-3">
            <h2 className="text-white font-bold text-lg">
              {selectedCategories.length === 1
                ? `${AUDIO_COLORS[selectedCategories[0]]?.emoji} ${AUDIO_COLORS[selectedCategories[0]]?.name}`
                : selectedCategories.length > 1
                  ? `🎨 ${selectedCategories.length} temáticas`
                  : '🎧 Escucha'}
            </h2>
            <div className="flex items-center gap-2">
              <div className="flex bg-white/10 rounded-lg p-0.5">
                <button
                  onClick={() => setFeedSort('recent')}
                  className={`px-2 py-1 rounded-md text-xs transition-all ${feedSort === 'recent' ? 'bg-purple-500 text-white' : 'text-white/60'}`}
                >
                  Recientes
                </button>
                <button
                  onClick={() => setFeedSort('popular')}
                  className={`px-2 py-1 rounded-md text-xs transition-all ${feedSort === 'popular' ? 'bg-purple-500 text-white' : 'text-white/60'}`}
                >
                  Populares
                </button>
              </div>
              {(selectedCategories.length > 0 || selectedLanguage || selectedShape) && (
                <button 
                  onClick={clearFilters}
                  className="text-red-400 text-xs"
                >
                  Limpiar filtros
                </button>
              )}
            </div>
          </div>

          {/* Indicador de reproducción infinita */}
          {currentAudio && filteredAudios.length > 0 && (
            <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl p-2 mb-3 flex items-center gap-2 border border-purple-500/30">
              <Volume2 size={16} className="text-purple-400 animate-pulse" />
              <span className="text-white/70 text-xs">
                {selectedCategories.length === 1
                  ? `📚 Recopilatorio de ${AUDIO_COLORS[selectedCategories[0]]?.name} • ${filteredAudios.length} audios ${shuffleMode ? 'en orden aleatorio' : 'en orden'}`
                  : `Reproducción infinita • ${filteredAudios.length} audios ${shuffleMode ? 'en orden aleatorio' : 'en orden'}`}
              </span>
            </div>
          )}
          
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full" />
            </div>
          ) : filteredAudios.length === 0 ? (
            <div className="bg-white/10 backdrop-blur rounded-2xl p-8 text-center border border-white/10">
              <p className="text-white/60">No hay audios con estos filtros</p>
              <button 
                onClick={clearFilters}
                className="mt-2 text-purple-400 text-sm"
              >
                Ver todos los audios
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredAudios.slice(0, 20).map((audio, idx) => {
                const colorData = AUDIO_COLORS[audio.color] || AUDIO_COLORS.blue;
                const isCurrentPlaying = currentAudio?.audio_id === audio.audio_id;
                const isActiveAndPlaying = isCurrentPlaying && isPlaying;
                const emojiTags = getEmojiTags(audio.emoji);
                const langFlag = LANGUAGE_FLAGS[audio.language]?.flag || '🇪🇸';
                
                return (
                  <div
                    key={audio.audio_id}
                    onClick={() => playAudio(audio, idx)}
                    className={`rounded-xl overflow-hidden cursor-pointer transition-all ${
                      isCurrentPlaying ? 'ring-2 ring-green-400' : 'hover:bg-white/10'
                    }`}
                    data-testid={`audio-card-${audio.audio_id}`}
                  >
                    <div className={`flex items-center gap-3 p-2 ${isCurrentPlaying ? 'bg-white/15' : 'bg-white/5'}`}>
                      {/* Badge: forma del tipo de audio + color de temática */}
                      <div className="relative flex-shrink-0">
                        <AudioVisualBadge
                          shape={audio.shape}
                          color={audio.color}
                          emoji={audio.emoji || colorData.emoji}
                          language={audio.language}
                          size="sm"
                        />
                        {isActiveAndPlaying && (
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                            <AudioVisualizer isPlaying={true} bars={5} size="sm" />
                          </div>
                        )}
                      </div>
                      
                      {/* Info central */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-white font-medium text-sm">
                            {audio.title || colorData.name}
                          </p>
                          {audio.is_official && (
                            <span className="bg-purple-500 text-white text-[9px] px-1.5 py-0.5 rounded-full font-bold">
                              ✓ OFICIAL
                            </span>
                          )}
                          <span className="text-white/50 text-xs">•</span>
                          <span className="text-white/50 text-xs">{audio.duration_seconds}s</span>
                          <span className="text-base">{langFlag}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Link 
                            to={`/usuario/${audio.user_id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="flex items-center gap-1.5 hover:underline"
                          >
                            {audio.user_picture ? (
                              <img src={audio.user_picture} alt="" className="w-4 h-4 rounded-full" />
                            ) : (
                              <div className="w-4 h-4 rounded-full bg-white/30 flex items-center justify-center text-[8px] text-white font-bold">
                                {audio.username?.charAt(0)?.toUpperCase()}
                              </div>
                            )}
                            <span className="text-white/60 text-xs">@{audio.username?.split(' ')[0]}</span>
                          </Link>
                          {emojiTags.length > 0 && (
                            <div className="flex gap-0.5">
                              {emojiTags.slice(0, 3).map((emoji, i) => (
                                <span key={i} className="text-sm">{emoji}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Botón compartir (ECO) */}
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          await shareAudio(audio, window.location.origin);
                        }}
                        className="w-9 h-9 rounded-full bg-cyan-500/80 flex items-center justify-center flex-shrink-0 hover:bg-cyan-400"
                        title="Compartir (ECO)"
                        data-testid={`share-${audio.audio_id}`}
                      >
                        <Share2 size={16} className="text-white" />
                      </button>
                      
                      {/* Botón play */}
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isActiveAndPlaying ? 'bg-green-500' : 'bg-white/20'
                      }`}>
                        {isActiveAndPlaying ? (
                          <Pause size={18} className="text-white" />
                        ) : (
                          <Play size={18} className="text-white ml-0.5" />
                        )}
                      </div>
                    </div>

                    {/* Barra de progreso (solo para el audio actual) */}
                    {isCurrentPlaying && (
                      <div className="h-1 bg-white/10">
                        <div 
                          className="h-full bg-gradient-to-r from-green-400 to-emerald-500 transition-all duration-200"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>

      {/* Mini Player Mejorado con barra de progreso */}
      {currentAudio && (
        <div className="fixed top-14 left-2 right-2 bg-black/95 backdrop-blur-xl border border-white/10 z-40 rounded-2xl overflow-hidden">
          {/* Barra de progreso superior */}
          <div className="h-1 bg-white/10">
            <div 
              className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-200"
              style={{ width: `${progress}%` }}
            />
          </div>

          <div className="p-3">
            <div className="max-w-lg mx-auto flex items-center gap-2">
              <div className="relative flex-shrink-0">
                <AudioVisualBadge
                  shape={currentAudio.shape}
                  color={currentAudio.color}
                  emoji={currentAudio.emoji || AUDIO_COLORS[currentAudio.color]?.emoji}
                  language={currentAudio.language}
                  size="sm"
                />
                {isPlaying && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <AudioVisualizer isPlaying={true} bars={5} size="sm" />
                  </div>
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate flex items-center gap-2">
                  {currentAudio.title || AUDIO_COLORS[currentAudio.color]?.name}
                  {currentAudio.is_official && (
                    <span className="bg-purple-500 text-white text-[8px] px-1 py-0.5 rounded font-bold">✓</span>
                  )}
                </p>
                <p className="text-white/50 text-xs">
                  {LANGUAGE_FLAGS[currentAudio.language]?.flag} @{currentAudio.username?.split(' ')[0]} • {formatTime(audioRef.current?.currentTime || 0)}/{formatTime(duration)}
                </p>
              </div>

              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  const result = await shareAudio(currentAudio, window.location.origin);
                  if (result.success) {
                    const btn = e.currentTarget;
                    btn.querySelector('span').textContent = '✓';
                    setTimeout(() => btn.querySelector('span').textContent = 'ECO', 1500);
                  }
                }}
                className="bg-cyan-500 text-white px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1"
              >
                <Share2 size={14} />
                <span>ECO</span>
              </button>
              
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isPlaying ? 'bg-green-500' : 'bg-purple-500'
                }`}
              >
                {isPlaying ? <Pause size={20} className="text-white" /> : <Play size={20} className="text-white ml-0.5" />}
              </button>
              
              <button
                onClick={() => setShuffleMode(!shuffleMode)}
                className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                  shuffleMode ? 'bg-purple-500' : 'bg-white/10'
                }`}
                title={shuffleMode ? 'Aleatorio: activado (toca para escuchar en orden)' : 'En orden (toca para aleatorio)'}
                data-testid="shuffle-toggle"
              >
                {shuffleMode
                  ? <Shuffle size={16} className="text-white" />
                  : <Repeat size={16} className="text-white" />}
              </button>

              <button
                onClick={playNext}
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0"
                title="Siguiente audio"
              >
                <SkipForward size={16} className="text-white" />
              </button>
            </div>
          </div>
          
          {/* Audio principal */}
          <audio
            ref={audioRef}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
            onEnded={playNext}
          />
          
          {/* Audio pre-cargado (oculto) */}
          <audio ref={nextAudioRef} preload="auto" style={{ display: 'none' }} />
        </div>
      )}
    </div>
  );
};

export default HomePage;
